import React, { createContext, useContext, useState } from 'react';
import { useCSS } from '../../lib/hooks';

import { SquaredButton } from '../builder/ActionButton';
import ButtonCollection from '../builder/ButtonCollection';
import Input from '../builder/Input';

import ToggleContent from './ToggleContent';

const JsonEditContext = createContext();

const parseTree = (json, key = undefined) => {
	const tree = [];
	if(Array.isArray(json)) {
		json.map((v, i) => {
			tree.push({
				key:`${key}.${i}`,
				items:parseTree(v,`${key}.${i}`),
			});
		});

		return tree;
	}
	
	Object.keys(json).map((k) => {
		const final_key = key == undefined ? k : `${key}.${k}`;
		if(typeof json[k] == 'object' && json[k] !== null) {
			tree.push({
				key:final_key,
				items:parseTree(json[k],k)
			});
			return;
		}
		tree.push({ key:final_key, name:k, value:json[k] });
	});

	return tree;
};

const checkPath = (key, paths_include, paths_exclude) => {
	const key_parts = key.split(".");

	if(paths_exclude != false) {
		for(var p in paths_exclude) {
			const path_parts = paths_exclude[p].split(".");

			let path_match = true;
			for(var i in key_parts) {
				if(path_parts[i] == undefined) break;

				if(key_parts[i] != path_parts[i]) {
					path_match = false;
					break;
				}
			}

			if(path_match && key_parts.length == path_parts.length) return false;
		}
	}

	if(paths_include != false) {    
		for(let p in paths_include) {
			const path_parts = paths_include[p].split(".");
			
			let path_match = true;
			for(var i in key_parts) {
				if(path_parts[i] == undefined) break;
				if(path_parts[i] == "*") continue;
	
				if(key_parts[i] != path_parts[i]) {
					path_match = false;
					break;
				}
			}

			if(path_match) return true;
		}

		return false;
	}

	return true;
};

const JsonProp = ({ item, level }) => {
	const { isEditing, setIsEditing, editTree, include_fields, exclude_fields } = useContext(JsonEditContext);
	const [ value, setValue ] = useState(item.value);

	if(level == undefined) level = 1;

	if(include_fields !== false || exclude_fields !== false) {
		if(!checkPath(item.key, include_fields, exclude_fields)) return null;
	}

	const num_levels = (item.key.indexOf(".") > -1) ? item.key.split(".").length : 1;

	if(item?.items != undefined) {
		return (
			<>
				{item.items.sort((a, b) => {
					// move article to the top (random sorting):
					if(a.name != 'article') return 0;
					return (a.name < b.name) ? -1 : 1;
				}).map((v, i) => {
					if(v.items != undefined) {
						const item_number_arr = v.key.split(".");
						const item_number = item_number_arr[item_number_arr.length - 1];

						const classes = ['item_props'];
						if(i > 0) classes.push('not_in_top');

						return (
							<div key={i} class={classes.join(" ")}>
								<strong>Item {parseInt(item_number) + 1}</strong>

								<JsonProp
									item={v}
									level={level + 1}
								/>
							</div>
						)
					}

					return (
						<JsonProp
							key={i}
							item={v}
							level={level + 1}
						/>
					)
				})}
			</>
		)
	}

	if(level < num_levels - 1) return null;

	return (
		<div class="fields_list level">
			<div class="row_level">
				<div class="l">{item.name}:</div>

				<ToggleContent
					onEditing={setIsEditing}
					onSave={() => editTree(item.key, value)}
					onClose={() => setValue(item.value)}
				>
					<ToggleContent.Display>
						<div class="v">{value}</div>
					</ToggleContent.Display>

					<ToggleContent.EditButton>
						<SquaredButton
							icon='knk-edit'
						/>
					</ToggleContent.EditButton>

					<ToggleContent.Edit value={value}>
						<div class="v">
							<Input
								name='v'
								value={value}
								change={(k, v) => setValue(v)}
								_class={{ first:true }}
							/>
						</div>

						<ButtonCollection>
							<ToggleContent.SaveButton>
								<SquaredButton
									icon='knk-on greenButton'
									color='white'
								/>
							</ToggleContent.SaveButton>

							<ToggleContent.CloseButton>
								<SquaredButton
									icon='knk-off'
									color='red'
								/>
							</ToggleContent.CloseButton>
						</ButtonCollection>
					</ToggleContent.Edit>
				</ToggleContent>
			</div>
		</div>
	)
};

const JSONEdit = ({ json, ...props }) => {
	useCSS('comp/json_editor.css');

	const [ new_json, setNewJson ] = useState(json);
	const [ json_tree, setJsonTree ] = useState(parseTree(json));
	const [ isEditing, setIsEditing ] = useState(false);

	const include_fields = props?.include_fields ?? false;
	const exclude_fields = props?.exclude_fields ?? false;

	const editTree = (key, value) => {
		const parts = key.split(".");

		let val = new_json;
		for(let i = 0; i < parts.length - 1; i++) {
			val = val[parts[i]];
		}
		val[parts[parts.length - 1]] = value;

		setNewJson({ ...new_json });
		setJsonTree(parseTree(new_json));

		if(props?.onSave) props.onSave(new_json);
	};

	return (
		<JsonEditContext.Provider
			value={{ isEditing, setIsEditing, editTree, include_fields, exclude_fields }}
		>
			<div class="json_parent">
				{json_tree.map((v, i) => {
					return (
						<JsonProp
							key={i}
							item={v}
						/>
					)
				})}
			</div>
		</JsonEditContext.Provider>
	)
};

export default JSONEdit;